import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, UserPlus, Trash2, RefreshCw, ShieldCheck, Eye, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useUserRoles } from '@/hooks/useUserRoles';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface CompanyUser {
  id: string;
  role_id: string;
  role: string;
  email: string;
  created_at: string;
  can_manage: boolean;
}

export default function TeamAccessSettings({ companyId }: { companyId: string }) {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('admin');
  const [expiryDays, setExpiryDays] = useState<string>('30');
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [userToRemove, setUserToRemove] = useState<CompanyUser | null>(null);
  const { isOwner, isAdmin, loading: rolesLoading } = useUserRoles(companyId);

  async function loadUsers() {
    setRefreshing(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-list-company-users', {
        body: { companyId },
      });

      if (error) {
        toast.error('Failed to load team members');
        console.error(error);
      } else {
        setUsers(data.users || []);
        setCanManage(data.can_manage || false);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      toast.error('Failed to load team members');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }

  async function inviteUser() {
    if (!email) {
      toast.error('Email is required');
      return;
    }

    if (!role) {
      toast.error('Role is required');
      return;
    }

    setInviting(true);
    try {
      const requestBody: any = { companyId, email, role };

      // Add expiry for observer roles
      if (role === 'observer') {
        requestBody.expiryDays = parseInt(expiryDays);
      }

      const { data, error } = await supabase.functions.invoke('admin-invite-company-user', {
        body: requestBody,
      });

      if (error) {
        toast.error(error.message || 'Failed to invite user');
      } else {
        const expiryMessage = role === 'observer' ? ` (expires in ${expiryDays} days)` : '';
        toast.success(data.message || `User invited successfully${expiryMessage}`);
        setEmail('');
        setRole('admin');
        setExpiryDays('30');
        loadUsers();
      }
    } catch (err) {
      console.error('Error inviting user:', err);
      toast.error('Failed to invite user');
    } finally {
      setInviting(false);
    }
  }

  async function updateUserRole(roleId: string, newRole: string) {
    try {
      const { error } = await supabase.functions.invoke('admin-update-user-role', {
        body: { roleId, newRole, companyId },
      });

      if (error) {
        toast.error(error.message || 'Failed to update role');
      } else {
        toast.success('Role updated successfully');
        loadUsers();
      }
    } catch (err) {
      console.error('Error updating role:', err);
      toast.error('Failed to update role');
    }
  }

  async function removeUser(user: CompanyUser) {
    try {
      const { error } = await supabase.functions.invoke('admin-remove-user-role', {
        body: { roleId: user.role_id, companyId },
      });

      if (error) {
        toast.error(error.message || 'Failed to remove user');
      } else {
        toast.success('User removed successfully');
        loadUsers();
      }
    } catch (err) {
      console.error('Error removing user:', err);
      toast.error('Failed to remove user');
    } finally {
      setUserToRemove(null);
    }
  }

  useEffect(() => {
    if (companyId && !rolesLoading) {
      loadUsers();
    }
  }, [companyId, rolesLoading]);

  const hasManagePermission = isOwner(companyId) || isAdmin(companyId);

  if (loading || rolesLoading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle>Team & Access Management</CardTitle>
            {!hasManagePermission && (
              <Badge variant="secondary" className="gap-1">
                <Eye className="h-3 w-3" />
                Read Only
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={loadUsers} disabled={refreshing}>
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {hasManagePermission && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <ShieldCheck className="h-4 w-4" />
                <span>Invite new team members</span>
              </div>
              <div className="grid sm:grid-cols-4 gap-2">
                <Input
                  placeholder="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={inviting}
                />
                <Select value={role} onValueChange={setRole} disabled={inviting}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Founder</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="auditor">Auditor</SelectItem>
                    <SelectItem value="regulator">Regulator</SelectItem>
                    <SelectItem value="observer">Observer (Read-Only)</SelectItem>
                  </SelectContent>
                </Select>
                {role === 'observer' && (
                  <Select value={expiryDays} onValueChange={setExpiryDays} disabled={inviting}>
                    <SelectTrigger>
                      <SelectValue placeholder="Expiry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="180">180 days</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Button
                  onClick={inviteUser}
                  disabled={inviting}
                  className={role !== 'observer' ? 'sm:col-span-1' : ''}
                >
                  {inviting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}
                  Invite
                </Button>
              </div>
            </div>
          )}

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Added</TableHead>
                  {hasManagePermission && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={hasManagePermission ? 4 : 3}
                      className="text-center text-muted-foreground py-8"
                    >
                      No team members found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.role_id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>
                        {hasManagePermission ? (
                          <Select
                            value={user.role}
                            onValueChange={(value) => updateUserRole(user.role_id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owner">Founder</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="investor">Investor</SelectItem>
                              <SelectItem value="auditor">Auditor</SelectItem>
                              <SelectItem value="regulator">Regulator</SelectItem>
                              <SelectItem value="observer">Observer (Read-Only)</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="secondary">
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      {hasManagePermission && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUserToRemove(user)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!userToRemove} onOpenChange={() => setUserToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{userToRemove?.email}</strong> from this
              company? They will lose all access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToRemove && removeUser(userToRemove)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
